<template>
  <table class="table border-bottom table-bordered table-sm">
    <thead>
      <tr class="row1">
        <th rowspan="2" class="text-center bg-light col1">{{ $t('カテゴリー名')}}</th>
        <th rowspan="2" class="text-center bg-light col2">{{ $t('スキル名') }}</th>
        <th rowspan="2" class="text-center text-nowrap bg-light col3">{{ $t('配点倍数') }}</th>
        <th v-for="user in evaluatees" :colspan="numColsPerStaff" class="text-center bg-light bg-light">
          {{ user.name }}
        </th>
      </tr>
      <tr class="row2">
        <template v-for="user in evaluatees">
          <th class="text-center bg-light text-nowrap">
            {{ $t('自己') }}
          </th>
          <th v-for="n in numStages" class="text-center bg-light text-nowrap">
            {{ $t(`${n}次`) }}
          </th>
          <th class="text-center bg-light">
            {{ $t('最終') }}
          </th>
        </template>
      </tr>
    </thead>

    <tbody>
      <template v-for="skillCategory in job.skillCategories">
        <!--カテゴリー -->
        <tr>
          <td :rowspan="skillCategory.numSkills + 1" class="text-center col1" style="left: 0;">
            {{ skillCategory.name }}
          </td>
        </tr>

        <!--スキル -->
        <tr v-for="skill in skillCategory.skills">
          <td class="col2">{{ skill.name }}</td>
          <td class="text-center col3">{{ skill.formattedPoint }}</td>

          <!--各ユーザーの評価-->
          <template v-for="(user, index) in evaluatees">
            <!--自己評価-->
            <td class="text-center">
              {{ user.selfEvaluation.isClosed() ? formatScore(user.selfEvaluation, skill) : '' }}
              <i class="comment-readonly fas fa-comment ml-1 pointer" v-if="user.selfEvaluation.comments[skill.id]" :data-content="user.selfEvaluation.comments[skill.id]"></i>
            </td>

            <!--n次評価-->
            <template v-for="n in numStages">
              <td class="text-center">
                {{ user.otherEvaluations?.[n].isClosed() ? formatScore(user.otherEvaluations?.[n], skill) : '' }}
                <i class="comment-readonly fas fa-comment ml-1 pointer" v-if="user.otherEvaluations?.[n].comments[skill.id]" :data-content="user.otherEvaluations?.[n].comments[skill.id]"></i>
              </td>
            </template>

            <!--最終評価-->
            <td class="text-center" v-if="user.finalEvaluation.status !== 'CLOSED' && canEditEvaluations" style="min-width: 100px;">
              <div class="input-group">
                <select
                  :form="formId"
                  class="form-control form-control-sm score-select"
                  :name="`scores[${user.id}][0][${skill.id}]`"
                  :tabindex="index"
                  v-model="user.finalEvaluation.scores[skill.id]"
                >
                  <option></option>
                  <option v-for="rank in ranks" :value="rank.value">
                    {{ roundDecimal(rank.value * skill.point, 2) }}
                  </option>
                </select>
                <div class="input-group-append">
                  <button class="btn btn-light btn-sm border comment" style="z-index: 1" @click="openSkillComment(user.finalEvaluation, skill.id)">
                    <i class="fa-comment" :class="user.finalEvaluation.comments[skill.id] ? 'fas' : 'far'"></i>
                  </button>
                  <input :form="formId" type="hidden" :name="`skill_comments[${user.id}][0][${skill.id}]`" v-model="user.finalEvaluation.comments[skill.id]" />
                </div>
              </div>

              <input :form="formId" type="hidden" :name="`type[${user.id}]`" value="FINAL">
              <!--評価の数値が入力されていない時でもコメントが保存できるようにダミーのフォームを用意-->
              <input :form="formId" type="hidden" :name="`scores[${user.id}][0][dummy]`" value="">
            </td>
            <td class="text-center" v-else>
              {{ formatScore(user.finalEvaluation, skill) }}
              <i class="comment-readonly fas fa-comment ml-1 pointer" v-if="user.finalEvaluation.comments[skill.id]" :data-content="user.finalEvaluation.comments[skill.id]"></i>
            </td>

          </template>
        </tr>

      </template>

      <!--合計点-->
      <tr>
        <td class="col1 text-center" colspan="3">{{ $t('合計点') }}</td>
        <template v-for="user in evaluatees">
          <td class="text-center">{{ user.selfEvaluation.isClosed() ? user.selfEvaluation?.totalScore : '' }}</td>
          <td v-for="n in numStages" class="text-center">{{ user.otherEvaluations[n].isClosed() ? user.otherEvaluations[n]?.totalScore : '' }}</td>
          <td class="text-center">{{ user.finalEvaluation?.totalScore }}</td>
        </template>
      </tr>

      <!-- コピー -->
      <tr v-if="canEditEvaluations">
        <td colspan="3" class="col1 text-center">{{ $t('評価のコピー') }}</td>
        <template v-for="user in evaluatees">
          <td class="text-center">
            <button class="btn btn-sm btn-success" v-if="!user.finalEvaluation.isClosed() && user.selfEvaluation.isClosed()" @click="user.selfEvaluation.copy(user.finalEvaluation)">{{ $t('コピー') }}</button>
          </td>
          <td v-for="n in numStages" class="text-center">
            <button class="btn btn-sm btn-success" v-if="!user.finalEvaluation.isClosed() && user.otherEvaluations?.[n].isClosed()" @click="user.otherEvaluations?.[n].copy(user.finalEvaluation)">{{ $t('コピー') }}</button>
          </td>
          <td></td>
        </template>
      </tr>

      <!--コメント欄-->
      <tr>
        <td class="col1 text-center" :rowspan="numStages + 2" colspan="2">{{ $t('コメント') }}</td>
        <td class="col3 text-center">{{ $t('自己') }}</td>
        <td v-for="user in evaluatees" class="align-top" type="text" :colspan="numColsPerStaff">
          <p>{{ user.selfEvaluation?.status === 'CLOSED' ? user.selfEvaluation?.comment : '' }}</p>
        </td>
      </tr>
      <tr v-for="n in numStages">
        <td class="col3 text-center">{{ $t(`${n}次`) }}</td>
        <td v-for="user in evaluatees" class="align-top" type="text" :colspan="numColsPerStaff">
          <p>{{ user.otherEvaluations && user.otherEvaluations[n]?.status === 'CLOSED' ? user.otherEvaluations[n]?.comment : '' }}</p>
        </td>
      </tr>
      <tr>
        <td class="col3 text-center">{{ $t('最終') }}</td>
        <td v-for="user in evaluatees" class="align-top" type="text" :colspan="numColsPerStaff">
          <textarea
            v-if="user.finalEvaluation?.status !== 'CLOSED' && canEditEvaluations"
            :form="formId" :name="`comments[${user.id}][0]`"
            class="form-control"
            :placeholder="$t('コメント')"
            v-model="user.finalEvaluation.comment"
          ></textarea>
          <p v-else style="white-space: pre-wrap;">{{ user.finalEvaluation.comment }}</p>
        </td>
      </tr>

      <!--個人目標-->
      <template v-if="personalGoalStages?.length > 0">
        <tr v-for="(personalGoalStage, i) in personalGoalStages">
          <td :rowspan="personalGoalStages.length + 1" class="text-center col1" v-if="i === 0">{{ personalGoalTitle }}</td>
          <td class="col2" colspan="2">{{ personalGoalStage.name }}</td>

          <template v-for="user in evaluatees">
						<!--自己評価-->
            <td>
              <template v-if="user.selfEvaluation.isClosed()">
                <div v-for="goal in personalGoals[user.id]">
                  {{ goal.name }}：{{ personalGoalScores[user.id]?.['SELF']?.[personalGoalStage.id]?.[goal.id]?.score ?? '未入力'}}
                </div>
              </template>
            </td>
            <!-- n次評価 -->
            <template v-for="n in numStages">
              <td>
                <template v-if="personalGoals[user.id] && user.otherEvaluations?.[n].isClosed()">
                  <div v-for="goal in personalGoals[user.id]">
                    {{ goal.name }}：{{ personalGoalScores[user.id]?.['OTHER#' + n]?.[personalGoalStage.id]?.[goal.id]?.score ?? '未入力' }}
                  </div>
                </template>
              </td>
            </template>
            <!-- 最終評価 -->
            <td>
              <div v-if="personalGoals[user.id]" v-for="(goal, i) in personalGoals[user.id]" :class="{'mt-2': i !== 0}">
                {{ goal.name }}：
                <input :form="formId" :name="`personal_goal_scores[${user.id}][FINAL][${personalGoalStage.id}][${goal.id}][score]`"
                  v-if="!user.finalEvaluation.isClosed() && user.finalEvaluation?.status !== 'CLOSED' && canEditEvaluations"
                  class="form-control form-control-sm" type="number"
                  v-model="personalGoalScores[user.id]['FINAL'][personalGoalStage.id][goal.id]['score']"
                />
                <span v-else>{{ personalGoalScores[user.id]?.['FINAL']?.[personalGoalStage.id]?.[goal.id]?.['score'] }}</span>
              </div>
            </td>
          </template>
        </tr>
        <tr>
          <td colspan="2" class="col2">{{ $t('評価') }}</td>
          <td v-for="user in evaluatees" :colspan="numColsPerStaff" class="text-center">
            <div v-if="personalGoals[user.id]">
              <input :form="formId" :name="`personal_goals[${user.id}]`" v-if="!user.finalEvaluation.isClosed() && user.finalEvaluation?.status !== 'CLOSED' && canEditEvaluations" class="form-control text-center" type="number" step="0.01" v-model="personalGoalAchievementRates[user.id]" />
              <span v-else>{{ personalGoalAchievementRates[user.id] }} %</span>
            </div>
          </td>
        </tr>
      </template>

      <!-- 数値目標のヘッダー -->
      <template v-if="kpiEnabled">
        <tr>
          <td :rowspan="job.kpiWeights?.length + 4" class="text-center col1">{{ $t('数値目標') }}</td>
          <td rowspan="2" class="text-center col2 bg-light"></td>
          <td rowspan="2" class="text-center col3 bg-light"></td>
          <template v-for="user in evaluatees">
            <td colspan="2" class="text-center bg-light">{{ $t('ウェイト前') }}</td>
            <td rowspan="2" class="text-center bg-light" :colspan="numColsPerStaff - 2">{{ $t('ウェイト後') }}</td>
          </template>
        </tr>
        <tr>
          <template v-for="user in evaluatees">
            <td class="text-center bg-light" style="white-space: nowrap">{{ $t('計算値') }}</td>
            <td class="text-center bg-light" style="min-width: 100px">{{ $t('修正値') }}</td>
          </template>
        </tr>
      </template>

      <!--数値目標ON & 達成率表示-->
      <template v-if="kpiEnabled && !shouldDisplayTotalScore">
        <!--数値目標-->
        <tr v-for="(kpiWeight, i) in job.kpiWeights">
          <td class="text-center col2">{{ kpiWeight.kpi.name }}</td>
          <td class="text-center col3">{{ kpiWeight.value }} %</td>
          <template v-for="user in evaluatees">
            <td style="white-space: nowrap">
              <span v-if="summaryOfJobKpi?.[user.id]?.['calculatedValue']?.[kpiWeight?.kpi?.id]">
                {{ summaryOfJobKpi[user.id]['calculatedValue'][kpiWeight?.kpi?.id] }} %
              </span>
            </td>
            <td class="text-center" style="white-space: nowrap">
              <div v-if="!user.finalEvaluation.isClosed() && user.finalEvaluation?.status !== 'CLOSED' && canEditEvaluations" class="d-flex align-items-center">
                <input :form="formId" :name="`kpi_achievements[${user.id}][${kpiWeight.kpi.id}]`"
                       class="form-control form-control-sm" type="number" step="0.01" v-model="summaryOfJobKpi[user.id]['averages'][kpiWeight.kpi.id]"
                />
                <span class="ml-1">%</span>
              </div>
              <span v-else-if="summaryOfJobKpi?.[user.id]?.['weightedAverages']?.[kpiWeight.kpi.id]">
                {{ summaryOfJobKpi?.[user.id]?.['averages']?.[kpiWeight.kpi.id] }} %
               </span>
            </td>
            <td :colspan="numColsPerStaff - 2" class="text-center">
              <span v-if="summaryOfJobKpi?.[user.id]?.['weightedAverages']?.[kpiWeight.kpi.id]">
                {{ summaryOfJobKpi?.[user.id]?.['weightedAverages']?.[kpiWeight.kpi.id] }} %
              </span>
            </td>
          </template>
        </tr>

        <!--スキル評価-->
        <tr>
          <td class="text-center col2">{{ $t('スキル') }}</td>
          <td class="text-center col3">{{ job.skillWeight?.value ?? 0 }} %</td>
          <template v-for="user in evaluatees">
            <td colspan="2" class="text-center">
              {{ summaryOfJobKpi?.[user.id]?.['skillEvaluationAverage'] }} %
            </td>
            <td :colspan="numColsPerStaff - 2" class="text-center">
              {{ summaryOfJobKpi?.[user.id]?.['weightedSkillEvaluationAverage'] }} %
            </td>
          </template>
        </tr>

        <!-- 個人目標評価 -->
        <tr>
          <td class="text-center col2">{{ $t('個人目標') }}</td>
          <td class="text-center col3">{{ personalGoalWeights[initialJob.id]?.['value'] ?? 0 }} %</td>
          <template v-for="user in evaluatees">
            <td colspan="2" class="text-center">{{ personalGoalAchievementRates[user.id] ?? 0 }} %</td>
            <td :colspan="numColsPerStaff - 2" class="text-center">
              {{ weightedPersonalGoalAchievementRates[user.id] ?? 0 }} %
            </td>
          </template>
        </tr>

        <!--合計-->
        <tr>
          <td colspan="3" class="text-center col1">{{ $t('合計') }}</td>
          <td v-for="user in evaluatees" :colspan="numColsPerStaff" class="text-center">
            {{ roundDecimal(
            (summaryOfJobKpi?.[user.id]?.['total'] ?? 0) + (summaryOfJobKpi?.[user.id]?.['weightedSkillEvaluationAverage'] ?? 0) + (weightedPersonalGoalAchievementRates[user.id] ?? 0), 2)
            }} %
          </td>
        </tr>

        <!--特別評価-->
        <tr-additional-points
          :can-edit="canEditEvaluations"
          :evaluatees="evaluatees"
          :form-id="formId"
          :num-cols-per-staff="numColsPerStaff"
          :score-type="scoreType"
        />

        <!--最終評価-->
        <tr>
          <td colspan="3" class="text-center col1">{{ $t('最終評価') }}</td>
          <td v-for="user in evaluatees" :colspan="numColsPerStaff" class="text-center">
            {{ getKpiFinalResult(user, (job.skillWeight?.value ?? 0)) }} %
          </td>
        </tr>
      </template>

      <!--数値目標OFF & 達成率表示 -->
      <template v-else-if="!shouldDisplayTotalScore">
        <tr>
          <td colspan="3" class="text-center col1">{{ $t('合計') }}</td>
          <td v-for="user in evaluatees" :colspan="numColsPerStaff" class="text-center">
            {{ maxScore > 0
            ? roundDecimal((user.finalEvaluation?.totalScore ?? 0) / maxScore * 100, 2)
            : '' }} %
          </td>
        </tr>

        <!--特別評価-->
        <tr-additional-points
          :can-edit="canEditEvaluations"
          :evaluatees="evaluatees"
          :form-id="formId"
          :num-cols-per-staff="numColsPerStaff"
          :score-type="scoreType"
        />

        <!--最終評価-->
        <tr>
          <td colspan="3" class="text-center col1">{{ $t('最終評価') }}</td>
          <td v-for="user in evaluatees" :colspan="numColsPerStaff" class="text-center">
            {{ maxScore > 0
            ? roundDecimal((user.finalEvaluation?.totalScore ?? 0) / maxScore * 100 + user.additionalPoint, 2)
            : '' }} %
          </td>
        </tr>
      </template>

      <!--合計点表示の場合-->
      <template v-else-if="shouldDisplayTotalScore">
        <!--合計-->
        <tr>
          <td colspan="3" class="text-center col1">{{ $t('合計') }}</td>
          <td v-for="user in evaluatees" :colspan="numColsPerStaff" class="text-center">{{ user.finalEvaluation?.totalScore }} {{ $t('点') }}</td>
        </tr>

        <!--特別評価-->
        <tr-additional-points
          :can-edit="canEditEvaluations"
          :evaluatees="evaluatees"
          :form-id="formId"
          :num-cols-per-staff="numColsPerStaff"
          :score-type="scoreType"
        />

        <!--最終評価-->
        <tr>
          <td colspan="3" class="text-center col1">{{ $t('最終評価') }}</td>
          <td v-for="user in evaluatees" :colspan="numColsPerStaff" class="text-center">
            {{ (user.finalEvaluation?.totalScore ?? 0) + user.additionalPoint }} {{ $t('点') }}
          </td>
        </tr>
      </template>

      <!--最終評価を一時中断し、自己・他者評価の修正を許可する機能-->
      <tr v-if="canEditEvaluations">
        <td colspan="3" class="text-center col1">{{ $t('一時的に自己・N次評価の修正を許可') }}</td>
        <td v-for="user in evaluatees" :colspan="numColsPerStaff" class="text-center">
          <template v-if="user.finalEvaluation.status === undefined">
            {{ $t('最終評価開始前') }}
          </template>
          <template v-else-if="user.finalEvaluation.isClosed()">
            {{ $t('最終評価確定済み') }}
          </template>
          <template v-else-if="user.isFinalEvaluationStarted">
            <button
              form="pendingEvaluationForm"
              type="submit"
              class="btn btn-sm btn-success"
              name="user_id"
              :value="user.id"
            >
              {{ $t('許可') }}
            </button>
          </template>
          <template v-else>
            <button
              form="pendingEvaluationForm"
              type="submit"
              class="btn btn-sm btn-secondary"
              name="user_id"
              :value="user.id"
            >
              {{ $t('許可の取り消し') }}
            </button>
          </template>
        </td>
      </tr>

      <!--個別に最終評価の確定・修正をさせる-->
      <tr v-if="canEditEvaluations">
        <td colspan="3" class="text-center col1">{{ $t('評価を個別に確定・修正') }}</td>
        <td v-for="user in evaluatees" :colspan="numColsPerStaff" class="text-center">
          <template v-if="user.finalEvaluation.isClosed()">
            <button
              form="reopenAnEvaluationForm"
              type="submit"
              class="btn btn-sm btn-primary"
              name="user_id"
              :value="user.id"
            >
              {{ $t('修正') }}
            </button>
          </template>
          <template v-else>
            <button class="btn btn-sm btn-primary" @click="closeAnEvaluation(user.id)">
              {{ $t('確定') }}
            </button>
          </template>
        </td>
      </tr>

    </tbody>
  </table>

  <!-- Popover用のテンプレート -->
  <div style="display: none;">
    <div ref="template">
      <textarea class="form-control form-control-sm mb-2" rows="4" v-model="skillComment"></textarea>
      <div class="text-right">
        <button class="btn btn-primary btn-sm" @click="saveSkillComment(skillComment)">設定</button>
      </div>
    </div>
  </div>
</template>

<script>
import Job from "../../../models/Job";
import Evaluator from "../../../models/Evaluator";
import TrAdditionalPoints from "./TrAdditionalPoints.vue";

export default {
  components: {TrAdditionalPoints},
  props: {
    initialEvaluator: {
      type: Object,
      required: true,
    },
    initialJob: {
      type: Object,
      required: true,
    },
    initialEvaluations: {
      type: Object,
      required: true,
    },
    numStages: {
      type: Number,
      required: true,
    },
    ranks: {
      type: Array,
      required: true,
    },
    maxRankValue: {
      type: Number,
      required: true,
    },
    formId: {
      type: String,
      required: true,
    },
    shouldDisplayTotalScore: {
      type: Boolean,
      default: false,
    },
    additionalPoints: {
      type: Object,
      required: false,
    },
    kpiEnabled: {
      type: Boolean,
      default: false,
    },
    summaryOfJobKpi: {
      type: Object,
      required: false,
    },
    canEditEvaluations: {
      type: Boolean,
      default: true,
    },
    scoreType: {
      type: String,
      required: true
    },
    personalGoals: {
      type: Object,
      required: true
    },
    personalGoalScores: {
      type: Object,
      required: true
    },
    personalGoalStages: {
      type: Array,
      required: true
    },
    personalGoalTitle: {
      type: String,
      required: false
    },
    personalGoalWeights: {
      type: Object,
      required: false
    },
    evaluationType: {
      type: String,
      required: false
    },
    personalGoalAchievementRates: {
      type: Object,
      required: false
    },
    weightedPersonalGoalAchievementRates: {
      type: Object,
      required: false
    }
  },

  data() {
    return {
      evaluator: null,
      job: {},
      numColsPerStaff: this.numStages + 2, // ステージ数 + 自己評価 + 最終評価
      skillComment: '',
      skillCommentTarget: {}
    }
  },

  computed: {
    evaluatees() {
      return this.evaluator?.evaluatees;
    },
    maxScore() {
      return this.job.getMaxScore(this.maxRankValue);
    }
  },

  mounted() {
    const self = this;
    this.job = new Job(this.initialJob);
    this.evaluator = new Evaluator(this.initialEvaluator);
    this.evaluator.loadEvaluatees(this.initialJob.users);

    // 被評価者それぞれの評価・特別評価を読み込む
    Object.keys(this.evaluatees).forEach(userId => {
      const user = this.evaluatees[userId];
      user?.loadEvaluations(this.initialEvaluations[userId], this.numStages);
      if (this.additionalPoints) {
        user?.loadAdditionalPoint(this.additionalPoints[userId]?.value);
      }
    });

    // FIXME: 純粋なVueに移植する
    $(function() {
      $('.comment').popover({
        container: 'body',
        html: true,
        sanitize: false,
        trigger: 'manual',
        content: self.$refs.template
      });
      $('.comment').on('click', function() {
        $('.comment').popover('hide');
        $(this).popover('show');
      });
      $('.comment-readonly').popover({
        container: 'body'
      });
    });
  },

  methods: {
    /**
     * 評価をフォーマットする
     * 評価未入力の場合は空文字を出力する
     *
     * @param {Evaluation|null} evaluation
     * @param {Skill} skill
     */
    formatScore(evaluation, skill) {
      if (!evaluation) {
        return '';
      }
      const score = evaluation?.getScoreBySkillId(skill.id);
      return score !== null ? this.roundDecimal(score * skill.point, 2) : '';
    },

    /**
     * 指定のユーザーの数値目標の最終評価を取得する
     *
     * @param {User} user
     * @param {number} skillWeight
     */
    getKpiFinalResult(user, skillWeight) {
      const userSummary = this.summaryOfJobKpi?.[user.id];
      const res = (userSummary?.['total'] ?? 0) + user.additionalPoint;

      return this.roundDecimal(res + (userSummary?.['weightedSkillEvaluationAverage'] ?? 0), 2);
    },

    /**
     * 小数点第decimalPlace位までvalueを丸めて返す
     *
     * @param {number} value
     * @param {number} decimalPlace
     */
    roundDecimal(value, decimalPlace) {
      const decimalPart = decimalPlace > 0 ? Math.pow(10, decimalPlace) : 1;
      return Math.round(value * decimalPart) / decimalPart;
    },

    /**
     * 指定のユーザーの評価を個別で確定させる
     *
     * @param {number} userId
     */
    closeAnEvaluation(userId) {
      const form = document.getElementById(this.formId);

      const selects = Array.from(document.querySelectorAll(`select[name^="scores[${userId}]"]`)).reverse();
      const isAllFilled = selects.every(select => select.value !== '');

      if (!isAllFilled) {
        selects.forEach(select => {
          if (!select.value) {
            select.setCustomValidity('評価が入力されていません');
            select.reportValidity();
          }
        });
      }
      else {
        form.status.value = 'CLOSED';
        form.user_id.value = userId;

        form.submit();
      }
    },

    /**
     * スキル別コメント用ポップオーバーを表示
     * FIXME: 現在、表示処理自体はJQueryの枠組みでやっている。
     *
     * @param evaluation
     * @param skillId
     */
    openSkillComment(evaluation, skillId) {
      this.skillComment = evaluation.comments[skillId];
      this.skillCommentTarget = { evaluation, skillId };
    },

    /**
     * スキル別コメントを設定する
     *
     * @param comment
     */
    saveSkillComment(comment) {
      const evaluation = this.skillCommentTarget.evaluation;
      const skillId = this.skillCommentTarget.skillId;
      evaluation.comments[skillId] = comment;
      $('.comment').popover('hide');
    }
  }
}
</script>
